<template>
	<popup
		ref="popup"
		class="payment-popup-holder"
		:class="{ 'payment-popup-holder--dark': darkTheme }"
	>
		<span
			v-if="paymentType === 'ApplePayCard'"
			class="card-holder"
			slot="opener"
		>
			<img :src="applePayIcon" alt="" class="card-holder__image" />
			Apple Pay
		</span>
		<span
			v-else-if="paymentType === 'AndroidPayCard'"
			class="card-holder"
			slot="opener"
		>
			<img :src="googlePayIcon" alt="" class="card-holder__image" />
			Google Pay
		</span>
		<span v-else class="card-holder" slot="opener">
			<span class="card-image" :class="creditCardType" />
			<span class="card-number">*{{ creditCardNumbers }}</span>
		</span>

		<template slot="content">
			<div class="payment-popup-title-wrapper">
				<span class="payment-popup-title">Payment Method</span>
				<close-btn :color="darkTheme && '#fff'" @close="hide" />
			</div>

			<div class="payment-popup-card">
				<span v-if="paymentType === 'ApplePayCard'" class="card-holder">
					<img :src="applePayIcon" alt="" class="card-holder__image" />
					Apple Pay
				</span>
				<span v-else-if="paymentType === 'AndroidPayCard'" class="card-holder">
					<img :src="googlePayIcon" alt="" class="card-holder__image" />
					Google Pay
				</span>
				<span v-else class="card-holder card-holder--image">
					<span class="card-image" :class="creditCardType" />
					<span>****{{ creditCardNumbers }}</span>
				</span>
			</div>

			<span class="payment-popup-item" @click="$emit('open-credit-card-modal')">
				Use Other Credit Card
			</span>
		</template>
	</popup>
</template>

<script>
import Popup from '@/components/modals/Popup'
import CloseBtn from '@/components/CloseBtn.vue'
import AppleIcon from '@/assets/images/ic_apple_pay.svg'
import GooglePayIcon from '@/assets/images/ic_google_pay.svg'

export default {
	name: 'PaymentMethodPopup',

	components: {
		CloseBtn,
		Popup
	},

	props: {
		creditCard: {
			type: Object,
			default: () => ({})
		},

		darkTheme: {
			type: Boolean,
			default: false
		}
	},

	computed: {
		paymentType() {
			return this.creditCard && this.creditCard.paymentGatewayType
		},

		creditCardNumbers() {
			return this.creditCard && this.creditCard.lastFour
		},

		creditCardType() {
			return (
				this.creditCard &&
				this.creditCard.cardType?.toLowerCase()?.replace(/[/\s]/g, '')
			)
		},

		applePayIcon() {
			return AppleIcon
		},

		googlePayIcon() {
			return GooglePayIcon
		}
	},

	methods: {
		hide() {
			if (this.$refs.popup) this.$refs.popup.hide()
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.payment-popup-holder {
	color: $grey-dark;

	.popup {
		padding-bottom: 2px;
		font-size: 16px;
	}

	.popup-opener {
		background-color: $background-popup;
		border: 1px solid $border-color;
		border-radius: 4px;
		margin-bottom: 15px;

		.card-holder {
			&:not(.card-holder--image) {
				padding-left: 15px;
			}
		}
	}

	.popup-opener:after {
		top: 16px;
	}

	&.opened .popup-opener:after {
		top: 18px;
	}

	.close-popup-btn {
		top: 8px;
	}

	&--dark {
		.card-number,
		.payment-popup-title,
		.payment-popup-card,
		.payment-popup-item,
		.card-holder {
			color: $white;
		}

		.popup-opener {
			&:after {
				border-color: $semi-dark-blue;
			}
		}

		.popup {
			background: $background-popup;
			border: 1px solid $border-color;
		}

		.payment-popup-card {
			color: $white;
			background: rgba($blue-faceless-void, 0.25);

			&:after {
				border-color: $white;
			}
		}
	}
}

.card-holder {
	display: flex;
	align-items: center;
	min-height: 28px;

	&__image {
		margin-right: 10px;
		max-width: 35px;
	}

	.card-image {
		display: inline-block;
		width: 44px;
		height: 28px;
		background-image: url('https://s3-us-west-2.amazonaws.com/s.cdpn.io/346994/card_sprite.png');
		background-size: 86px 458px;
		border-radius: 3px;
		background-position: -100px 0;
		background-repeat: no-repeat;

		&.visa {
			background-position: 0 -398px;
		}

		&.mastercard,
		&.mc {
			background-position: 0 -281px;
		}

		&.americanexpress,
		&.amex {
			background-position: 0 -370px;
		}

		&.discover,
		&.dcvr {
			background-image: url('~@/assets/images/ic_discover.jpg');
			background-position: 0 0;
			background-size: 44px 28px;
			margin-right: 5px;
		}

		&.maestro {
			background-position: 0 -251px;
		}

		&.jcb {
			background-position: 0 -221px;
		}

		&.dinersclub {
			background-position: 0 -133px;
		}
	}
}

.payment-popup-title-wrapper {
	position: relative;
	padding: 0 16px;
}

.payment-popup-title {
	display: block;
	padding: 0 0 10px;
	font-size: 20px;
	text-align: center;
	color: $grey;
}

.payment-popup-card {
	margin: 0 -16px;
	padding: 10px 16px;
	background: $powder-blue;
	position: relative;

	&:after {
		position: absolute;
		right: 17px;
		top: 17px;
		width: 13px;
		height: 6px;
		border: solid $semi-dark-blue;
		border-width: 0 0 2px 2px;
		transform: rotate(-45deg);
		content: '';
	}
}

.payment-popup-item {
	display: block;
	margin: 0 -16px;
	padding: 14px 16px;
	color: $grey-dark;
	text-decoration: none;
	border-bottom: 1px solid $foggy;
	cursor: pointer;

	&:last-child {
		border: 0;
	}
}

@media (max-width: 767px) {
	.payment-popup-holder {
		.popup {
			width: calc(100vw - 24px);
			bottom: 100%;
			margin: 0 0 10px;
		}
	}
}
</style>
